import { useNavigate } from 'react-router-dom';
import { UserRoles } from '@dynamika-shared/types/auth';
import { routesPath } from '@router/routes';
import { RETURN_TO_KEY, resolvePostLoginTarget } from '@router/roleAccess';
import { useEffect } from 'react';
import { getLastSelectedUser } from '@restore/auth/auth.saga';
import { useAuthSlice } from '@restore/auth/auth.slice';

const mapRolesToRoutes: Record<
  UserRoles,
  (typeof routesPath)[keyof typeof routesPath]
> = {
  [UserRoles.USER]: routesPath.profile,
  [UserRoles.SITE]: routesPath.subjectBase,
  [UserRoles.PROJECT_MANAGER]: routesPath.clinicalTrials,
  [UserRoles.ADMIN]: routesPath.clinicalTrials,
  [UserRoles.READER]: routesPath.reader,
  [UserRoles.IMAGING_SPECIALIST]: routesPath.qc,
  [UserRoles.SPONSOR]: routesPath.sponsor,
  [UserRoles.DATA_MANAGER]: routesPath.profile,
  [UserRoles.SUPPORT]: routesPath.clinicalTrials,
  [UserRoles.EXTERNAL_USER]: routesPath.externalUserSubjects,
  [UserRoles.VALIDATION_SPECIALIST]: routesPath.validationSpecialistSubjects,
};
export const HomePage = () => {
  const navigate = useNavigate();
  const [userData, setUserRole] = useAuthSlice((state, actions) => [
    state.data,
    actions.setUserRole,
  ]);
  const lastSelectedUser = getLastSelectedUser();

  useEffect(() => {
    const userRoles = userData?.['dynamika7/user_metadata']?.roles ?? [];
    const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
    const target = returnTo
      ? resolvePostLoginTarget(returnTo, userRoles)
      : null;

    if (target) {
      // Two-phase: setUserRole dispatches an async saga, so navigating in the
      // same tick would let the route guard read the stale selectedRole and
      // bounce to /404. Activate first, then navigate on the re-run that fires
      // once the saga has updated selectedRole. The key is cleared only at the
      // moment we actually navigate, keeping the redirect one-shot (REQ-6).
      const roleIsActive = userData?.selectedRole === target.role;

      if (!roleIsActive) {
        setUserRole(target.role);
        return;
      }

      sessionStorage.removeItem(RETURN_TO_KEY);
      navigate(target.path);
      return;
    }

    if (returnTo) {
      sessionStorage.removeItem(RETURN_TO_KEY);
    }

    if (
      !lastSelectedUser &&
      userData &&
      userData['dynamika7/user_metadata'].roles.length > 1
    ) {
      navigate(routesPath.selectRole);
      return;
    }
    if (userData?.selectedRole) {
      navigate(mapRolesToRoutes[userData.selectedRole]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, userData?.selectedRole]);

  return <div />;
};
